import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Mail,
  MailOpen,
  Trash2,
  CheckCheck,
  Reply,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from "@/components/ui/Card";
import { cn } from "@/lib/cn";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface MessageRecord {
  id: number;
  agency_id: number;
  from_user_id: number;
  to_user_id: number;
  subject: string | null;
  body: string | null;
  read: number;
  request_reply: number;
  confirm_receipt: number;
  receipt_confirmed: number;
  created_at: string;
  from_first_name?: string;
  from_last_name?: string;
  to_first_name?: string;
  to_last_name?: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function Inbox() {
  const queryClient = useQueryClient();
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const { data: inboxData, isLoading } = useQuery<{ data: MessageRecord[] }>({
    queryKey: ["messaging", "inbox"],
    queryFn: () => api.get<{ data: MessageRecord[] }>("/messaging/inbox"),
  });

  const markReadMutation = useMutation({
    mutationFn: (id: number) => api.put(`/messaging/${id}/read`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["messaging"] });
    },
  });

  const confirmReceiptMutation = useMutation({
    mutationFn: (id: number) => api.put(`/messaging/${id}/confirm-receipt`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["messaging"] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => api.delete(`/messaging/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["messaging"] });
    },
  });

  const messages = inboxData?.data ?? [];

  function handleExpand(msg: MessageRecord) {
    if (expandedId === msg.id) {
      setExpandedId(null);
    } else {
      setExpandedId(msg.id);
      if (!msg.read) {
        markReadMutation.mutate(msg.id);
      }
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <div
            key={i}
            className="h-16 animate-pulse rounded-lg border bg-muted"
          />
        ))}
      </div>
    );
  }

  if (messages.length === 0) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <Mail className="mx-auto h-10 w-10 text-muted-foreground" />
          <p className="mt-2 text-sm text-muted-foreground">
            No messages in your inbox.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">
          Inbox ({messages.length})
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="divide-y">
          {messages.map((msg) => (
            <div key={msg.id}>
              <button
                type="button"
                onClick={() => handleExpand(msg)}
                className={cn(
                  "flex w-full items-center gap-4 px-4 py-3 text-left text-sm transition-colors hover:bg-accent/50",
                  !msg.read && "bg-accent/20 font-medium",
                )}
              >
                {msg.read ? (
                  <MailOpen className="h-4 w-4 shrink-0 text-muted-foreground" />
                ) : (
                  <Mail className="h-4 w-4 shrink-0 text-primary" />
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <span className="truncate">
                      {msg.from_first_name ?? "System"}{" "}
                      {msg.from_last_name ?? ""}
                    </span>
                    <span className="shrink-0 text-xs text-muted-foreground">
                      {new Date(msg.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <p className="truncate text-muted-foreground">
                    {msg.subject || "(No subject)"}
                  </p>
                </div>
                <div className="flex items-center gap-1">
                  {msg.request_reply === 1 && (
                    <span title="Reply requested"><Reply className="h-3.5 w-3.5 text-amber-500" /></span>
                  )}
                  {msg.confirm_receipt === 1 && !msg.receipt_confirmed && (
                    <span title="Receipt confirmation needed"><CheckCheck className="h-3.5 w-3.5 text-blue-500" /></span>
                  )}
                  {expandedId === msg.id ? (
                    <ChevronUp className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                  )}
                </div>
              </button>

              {expandedId === msg.id && (
                <div className="border-t bg-muted/30 px-4 py-3">
                  <div className="mb-2 flex items-center gap-2 text-xs text-muted-foreground">
                    <span>
                      From: {msg.from_first_name ?? "System"}{" "}
                      {msg.from_last_name ?? ""}
                    </span>
                    <span>|</span>
                    <span>
                      {new Date(msg.created_at).toLocaleString()}
                    </span>
                  </div>
                  <p className="whitespace-pre-wrap text-sm">
                    {msg.body}
                  </p>
                  <div className="mt-3 flex gap-2">
                    {msg.confirm_receipt === 1 &&
                      !msg.receipt_confirmed && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={(e) => {
                            e.stopPropagation();
                            confirmReceiptMutation.mutate(msg.id);
                          }}
                        >
                          <CheckCheck className="mr-1 h-3.5 w-3.5" />
                          Confirm Receipt
                        </Button>
                      )}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteMutation.mutate(msg.id);
                      }}
                    >
                      <Trash2 className="mr-1 h-3.5 w-3.5" />
                      Delete
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
